import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Toast } from '../../ui/Toast';
import { LandingView } from './LandingView';
import { ExploreAttributesView } from './ExploreAttributesView';
import { ManageJourneysView } from './ManageJourneysView';
import { TestAnalyticsView } from './TestAnalyticsView';
import type { ClientAnalyticsConfig } from '../../../types/analytics-onboarding';
import { analyticsOnboardingAPI } from '../../../services/analytics-onboarding-api.service';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';

type View = 'landing' | 'explore' | 'manage' | 'test';

const STORAGE_KEY = 'analytics_onboarding_state';
const saveState = (state: { view: View }) => { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {} };
const loadState = (): { view: View } | null => { try { const r = sessionStorage.getItem(STORAGE_KEY); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearState = () => { try { sessionStorage.removeItem(STORAGE_KEY); } catch {} };

export const AnalyticsOnboardingPage: React.FC<PageComponentProps> = () => {
  const { errorMessage, handleError, clearError } = useErrorHandler();
  const saved = loadState();
  const [currentView, setCurrentView] = useState<View>(saved?.view || 'landing');
  const [viewHistory, setViewHistory] = useState<View[]>([]);
  const [clientConfig, setClientConfig] = useState<ClientAnalyticsConfig | null>(null);
  const [isCheckingConfig, setIsCheckingConfig] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);
  const [exploreJourneyId, setExploreJourneyId] = useState<string | null>(null);
  const [panelKeys, setPanelKeys] = useState<Record<string, number>>({});

  const viewHistoryRef = useRef<View[]>([]);
  useEffect(() => { viewHistoryRef.current = viewHistory; }, [viewHistory]);

  const navigateTo = useCallback((view: View) => {
    setViewHistory(prev => [...prev, currentView]);
    setCurrentView(view);
    setPanelKeys(prev => ({ ...prev, [view]: (prev[view] || 0) + 1 }));
    history.pushState({ __analyticsPage: true }, '', window.location.href);
  }, [currentView]);

  useEffect(() => {
    const handlePopState = () => {
      const hist = viewHistoryRef.current;
      if (hist.length > 0) {
        const prev = hist[hist.length - 1];
        setViewHistory(h => h.slice(0, -1));
        setCurrentView(prev);
        setPanelKeys(k => ({ ...k, [prev]: (k[prev] || 0) + 1 }));
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const getUserInfo = () => {
    try { const e = sessionStorage.getItem('loggedUserInfo'); return e ? JSON.parse(atob(e)) : null; } catch { return null; }
  };

  const fetchClientConfig = useCallback(async () => {
    const info = getUserInfo();
    const clientId = info?.entity_id || info?.name || '';
    try {
      const config = await analyticsOnboardingAPI.getClientConfig(clientId);
      setClientConfig(config);
    } catch {
      setClientConfig({ analytics_config: null, exchange_application_id: null });
    }
  }, []);

  useEffect(() => {
    fetchClientConfig().finally(() => setIsCheckingConfig(false));
  }, [fetchClientConfig]);

  useEffect(() => { saveState({ view: currentView }); }, [currentView]);

  useEffect(() => {
    const handleSidebarNav = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      if (detail?.route === '/dashboard/analytics-onboarding' || detail?.itemId === 'analytics-onboarding') {
        if (currentView !== 'landing') { setCurrentView('landing'); clearState(); }
      }
    };
    window.addEventListener('react-sidebar-navigate', handleSidebarNav);
    return () => window.removeEventListener('react-sidebar-navigate', handleSidebarNav);
  }, [currentView]);

  // Single entry point: initialize if needed, then go directly to manage
  const handleConfigure = useCallback(async () => {
    setIsInitializing(true);
    try {
      const info = getUserInfo();
      const legalName = info?.name || info?.entity_id || 'Equal Analytics Client';
      await analyticsOnboardingAPI.initializeAnalytics(legalName);
      // Refresh client config so ManageJourneysView has fresh credentials
      await fetchClientConfig();
      navigateTo('manage');
    } catch (err) {
      handleError(err, { component: 'AnalyticsOnboardingPage', action: 'initialize' });
    } finally {
      setIsInitializing(false);
    }
  }, [navigateTo, fetchClientConfig, handleError]);

  const handleExplore = useCallback(() => { setExploreJourneyId(null); navigateTo('explore'); }, [navigateTo]);
  const handleTest = useCallback(() => navigateTo('test'), [navigateTo]);

  const handleBack = useCallback(() => {
    if (viewHistory.length > 0) history.back();
    else { setCurrentView('landing'); clearState(); }
  }, [viewHistory]);

  return (
    <div>
      <div style={{ padding: '24px', maxWidth: 1280, margin: '0 auto' }}>
        {errorMessage && <Toast message={errorMessage} variant="error" onClose={clearError} />}

        {isCheckingConfig || isInitializing ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '120px 0', color: '#94a3b8' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 14, marginTop: 16, color: '#64748b' }}>
              {isInitializing ? 'Setting up analytics...' : 'Loading...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        ) : (
          <>
            {currentView === 'landing' && (
              <LandingView onConfigure={handleConfigure} onExplore={handleExplore} onTest={handleTest} />
            )}
            {currentView === 'manage' && clientConfig && (
              <ManageJourneysView key={panelKeys.manage || 0} clientConfig={clientConfig} onBack={handleBack} onTest={handleTest} onError={handleError} onExploreAttributes={(journeyId) => { setExploreJourneyId(journeyId); navigateTo('explore'); }} />
            )}
            {currentView === 'test' && clientConfig && (
              <TestAnalyticsView key={panelKeys.test || 0} clientConfig={clientConfig} onBack={handleBack} onConfigure={handleConfigure} onError={handleError} />
            )}
            {currentView === 'explore' && (
              <ExploreAttributesView key={panelKeys.explore || 0} onBack={handleBack} onManageJourneys={() => navigateTo('manage')} onError={handleError} clientConfig={clientConfig} initialJourneyId={exploreJourneyId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
